import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { HistoryEdu as SignIcon } from '@mui/icons-material';
import { makeApiCall } from '../../config/api';
import { ACTIVE_ESIGN_STATUSES, EMAIL_RE, apiError } from './esignUtils';

const customerName = (quote) => [quote?.first_name, quote?.last_name].filter(Boolean).join(' ').trim()
  || quote?.company_name
  || quote?.customer_name
  || '';

/**
 * Send a quote's latest PDF for in-house signature (TKT-401).
 * onSent({ message, severity }) is called after a send attempt that created a request.
 */
const SendForSignatureDialog = ({ open, quote, onClose, onSent }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [cc, setCc] = useState('');
  const [language, setLanguage] = useState('auto');
  const [message, setMessage] = useState('');
  const [days, setDays] = useState(30);
  const [touched, setTouched] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  // Ask the server whether a PDF exists: the list row can be out of date
  // (for example right after Generate PDF). 'checking' | 'ready' | 'missing'
  const [pdfState, setPdfState] = useState('checking');

  useEffect(() => {
    if (!open || !quote) return undefined;
    let cancelled = false;
    setPdfState('checking');
    (async () => {
      try {
        const res = await makeApiCall(`api/quotes/${quote.id}/pdf/latest`);
        if (!cancelled) setPdfState(res.ok ? 'ready' : 'missing');
      } catch {
        // Let the server decide when sending.
        if (!cancelled) setPdfState('ready');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, quote]);

  useEffect(() => {
    if (open && quote) {
      setName(customerName(quote));
      setEmail((quote.email_address || '').trim());
      setCc('');
      setLanguage('auto');
      setMessage('');
      setDays(30);
      setTouched(false);
      setError('');
    }
  }, [open, quote]);

  const emailTrimmed = email.trim();
  const emailError = !emailTrimmed
    ? t('esign.send.emailRequired')
    : !EMAIL_RE.test(emailTrimmed) ? t('esign.send.emailInvalid') : '';
  const nameError = name.trim() ? '' : t('esign.send.nameRequired');
  const hasActive = ACTIVE_ESIGN_STATUSES.includes(quote?.esign_status);
  const noEmailOnFile = !(quote?.email_address || '').trim();

  const handleSend = async () => {
    setTouched(true);
    if (emailError || nameError) return;
    setSending(true);
    setError('');
    try {
      const res = await makeApiCall(`api/esign/quotes/${quote.id}/send`, {
        method: 'POST',
        body: JSON.stringify({
          recipientName: name.trim(),
          recipientEmail: emailTrimmed,
          ccEmails: cc,
          message: message.trim() || undefined,
          language: language === 'auto' ? undefined : language,
          expiresInDays: days,
        }),
      });
      if (!res.ok) {
        setError(t('esign.send.failed', { error: await apiError(res, res.statusText) }));
        return;
      }
      const data = await res.json();
      onSent(data.emailSent
        ? { message: t('esign.send.sent', { email: data.recipient }), severity: 'success' }
        : { message: t('esign.send.sentEmailFailed', { email: data.recipient, error: data.emailError || '' }), severity: 'warning' });
    } catch (e) {
      setError(t('esign.send.failed', { error: e.message }));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('esign.send.title')}</DialogTitle>
      <DialogContent dividers>
        {quote && (
          <Stack spacing={2}>
            <Typography variant="body2">
              <strong>{t('esign.send.quote')}:</strong> {quote.quote_number}
              {'  ·  '}
              <strong>{t('esign.send.customer')}:</strong> {quote.customer_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">{t('esign.send.intro')}</Typography>
            {pdfState === 'missing' && <Alert severity="error">{t('esign.send.noPdf')}</Alert>}
            {noEmailOnFile && <Alert severity="warning">{t('esign.send.noEmailOnFile')}</Alert>}
            {hasActive && <Alert severity="info">{t('esign.send.replaceWarning')}</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('esign.send.name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                error={touched && !!nameError}
                helperText={touched ? nameError : ''}
              />
              <TextField
                label={t('esign.send.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched(true)}
                type="email"
                required
                fullWidth
                error={touched && !!emailError}
                helperText={touched ? emailError : ''}
              />
            </Stack>
            <TextField
              label={t('esign.send.cc')}
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              helperText={t('esign.send.ccHelp')}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="esign-language-label">{t('esign.send.language')}</InputLabel>
                <Select
                  labelId="esign-language-label"
                  label={t('esign.send.language')}
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                >
                  <MenuItem value="auto">{t('esign.send.languageAuto')}</MenuItem>
                  <MenuItem value="en">{t('esign.send.english')}</MenuItem>
                  <MenuItem value="es-mx">{t('esign.send.spanish')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={t('esign.send.expiresIn')}
                type="number"
                value={days}
                onChange={(e) => setDays(Math.max(1, Math.min(120, parseInt(e.target.value, 10) || 1)))}
                inputProps={{ min: 1, max: 120 }}
                sx={{ minWidth: 200 }}
              />
            </Stack>
            <TextField
              label={t('esign.send.message')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              inputProps={{ maxLength: 4000 }}
            />
            <Typography variant="caption" color="text.secondary">{t('esign.send.pdfNote')}</Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>{t('esign.detail.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || pdfState !== 'ready'}
          startIcon={sending ? <CircularProgress size={18} /> : <SignIcon />}
        >
          {sending ? t('esign.send.sending') : t('esign.send.send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendForSignatureDialog;
